/**
 * Estado de la suscripción y paywall de fin de trial (Fase 2).
 *
 * Contrato con el backend:
 *  - `subscription.status` puede ser 'expired' (con `expiredAt`) cuando vence
 *    la prueba de 30 días sin pago. listSubscriptions la devuelve.
 *  - Las requests bloqueadas responden HTTP 402 con body
 *    `{ error: 'SUBSCRIPTION_EXPIRED' }` (toleramos también 403 con el mismo
 *    `error`). El interceptor de api.js llama a `handleSubscriptionExpiredError`,
 *    que dispara un evento global (una sola vez) que escucha SubscriptionContext.
 *
 * Lógica pura (sin React) para poder testearla sin @testing-library.
 */

import { hasTrial } from './plans';

export const SUBSCRIPTION_EXPIRED_CODE = 'SUBSCRIPTION_EXPIRED';
export const SUBSCRIPTION_EXPIRED_EVENT = 'subscription-expired';
export const EXPIRED_ROUTE = '/subscription/expired';

// Rutas privadas que se pueden usar con la cuenta pausada. `/plans` se acepta
// como alias por si se agrega; la ruta real de PlanSelectionPage es
// `/plan-selection`. Login/logout viven fuera del layout privado.
export const EXPIRED_ALLOWED_PATHS = [
  EXPIRED_ROUTE,
  '/subscription',
  '/plan-selection',
  '/plans',
  '/login',
  '/logout',
];

// Roles que pueden pagar / activar el plan.
export const BILLING_ROLES = ['OWNER', 'ADMIN'];

/**
 * ¿El error de axios es un bloqueo por suscripción vencida?
 * @param {object} error error de axios
 * @returns {boolean}
 */
export function isSubscriptionExpiredError(error) {
  const status = error?.response?.status;
  const code = error?.response?.data?.error;
  return (status === 402 || status === 403) && code === SUBSCRIPTION_EXPIRED_CODE;
}

// Dedupe: el evento se dispara una sola vez hasta que el context lo resetea
// (tras pagar / refrescar), así N requests bloqueadas no generan N avisos.
let expiredNotified = false;

/** Vuelve a habilitar la notificación (llamar al salir del estado vencido). */
export function resetSubscriptionExpiredNotice() {
  expiredNotified = false;
}

/**
 * Dispara el evento global de suscripción vencida (una sola vez).
 * @param {object} [detail] body de la respuesta
 * @returns {boolean} true si se disparó, false si ya se había notificado
 */
export function notifySubscriptionExpired(detail) {
  if (expiredNotified || typeof window === 'undefined') return false;
  expiredNotified = true;
  window.dispatchEvent(new CustomEvent(SUBSCRIPTION_EXPIRED_EVENT, { detail: detail || {} }));
  return true;
}

/**
 * Handler para el interceptor de respuestas de axios.
 * @param {object} error error de axios
 * @returns {boolean} true si el error era SUBSCRIPTION_EXPIRED
 */
export function handleSubscriptionExpiredError(error) {
  if (!isSubscriptionExpiredError(error)) return false;
  notifySubscriptionExpired(error.response.data);
  return true;
}

const PAID_ACTIVE_STATUSES = ['active', 'canceling'];

/**
 * Prioridad de una suscripción (menor = más prioritaria):
 *  0 trial pago · 1 active/canceling pago · 2 expired · 3 FREE legacy · 4 resto
 *  (cancelled, past_due, etc.).
 */
export function subscriptionRank(sub) {
  if (!sub) return 99;
  const isFree = sub.planId === 'free';
  if (sub.status === 'trial' && !isFree) return 0;
  if (PAID_ACTIVE_STATUSES.includes(sub.status) && !isFree) return 1;
  if (sub.status === 'expired') return 2;
  if (isFree && (sub.status === 'active' || sub.status === 'trial' || sub.status === 'canceling')) {
    return 3;
  }
  return 4;
}

/**
 * Elige la suscripción vigente de la organización.
 * Orden: active/trial > expired > FREE legacy > cancelled; desempata por la
 * más reciente (`createdAt`).
 * @param {Array<object>} subscriptions
 * @returns {object|null}
 */
export function selectCurrentSubscription(subscriptions) {
  if (!Array.isArray(subscriptions) || subscriptions.length === 0) return null;
  const sorted = [...subscriptions].sort((a, b) => {
    const diff = subscriptionRank(a) - subscriptionRank(b);
    if (diff !== 0) return diff;
    return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
  });
  return sorted[0] || null;
}

/** ¿La suscripción está vencida (paywall)? */
export function isSubscriptionExpired(subscription) {
  return subscription?.status === 'expired';
}

const normalizePath = (pathname) => {
  const p = String(pathname || '/').split(/[?#]/)[0];
  return p.length > 1 ? p.replace(/\/+$/, '') : p;
};

/** ¿Se puede navegar a `pathname` con la cuenta pausada? */
export function isPathAllowedWhenExpired(pathname) {
  return EXPIRED_ALLOWED_PATHS.includes(normalizePath(pathname));
}

/**
 * Destino del route guard: EXPIRED_ROUTE si la cuenta está pausada y la ruta
 * no está permitida; null si puede seguir.
 */
export function getExpiredRedirect(pathname, isExpired) {
  if (!isExpired) return null;
  return isPathAllowedWhenExpired(pathname) ? null : EXPIRED_ROUTE;
}

/** ¿El rol puede pagar/activar el plan? (tolera roles en minúsculas) */
export function canManageBilling(role) {
  return BILLING_ROLES.includes(String(role || '').toUpperCase());
}

/**
 * Días de prueba que le quedan a una suscripción en trial (redondeo hacia
 * arriba, mínimo 0). null si no está en trial o no tiene trialEnd.
 */
export function getTrialDaysLeft(subscription, now = new Date()) {
  if (subscription?.status !== 'trial' || !subscription.trialEnd) return null;
  const end = new Date(subscription.trialEnd);
  if (Number.isNaN(end.getTime())) return null;
  return Math.max(0, Math.ceil((end - now) / 86400000));
}

/**
 * Qué hacer cuando una organización con suscripción elige un plan.
 *  - 'start-trial': FREE legacy → Starter/Profesional. `updateSubscription` con
 *    `trialEnabled: true`, SIN Mercado Pago (arranca 30 días sin cargo).
 *  - 'pay': suscripción vencida (paywall), aunque elija el mismo plan.
 *    `updateSubscription` con `paymentMethodId: 'pending'` + activate MP.
 *  - 'activate': mismo plan en trial → solo activate MP (el backend difiere el
 *    primer cobro al fin del trial). NO se llama a updateSubscription porque
 *    con paymentMethodId pasa el trial a active y cobra en el acto.
 *  - 'change': cambio de plan normal (update pending + activate MP).
 *  - 'none': mismo plan ya activo.
 * @param {object} currentSubscription
 * @param {object|string} targetPlan plan (o planId) elegido
 * @returns {'start-trial'|'pay'|'activate'|'change'|'none'}
 */
export function resolvePlanChangeAction(currentSubscription, targetPlan) {
  const targetId = typeof targetPlan === 'string' ? targetPlan : targetPlan?.planId;
  const current = currentSubscription || {};
  if (current.status === 'expired') return 'pay';
  if (current.planId === 'free' && targetId !== 'free' && hasTrial(targetPlan)) {
    return 'start-trial';
  }
  if (targetId === current.planId) {
    return current.status === 'trial' ? 'activate' : 'none';
  }
  return 'change';
}
